import React, { useMemo } from 'react';
import { useTickets } from '../../../contexts/TicketContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    Legend
} from 'recharts';
import {
    Users,
    Ticket,
    CheckCircle2,
    Clock,
    TrendingUp,
    AlertTriangle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getStaffDisplayName } from '../../../utils/staffDisplay';

const OverviewTab = () => {
    const { tickets } = useTickets();
    const { allUsers } = useAuth();

    // --- Metrics Calculation ---
    const metrics = useMemo(() => {
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        // Filter tickets last 24h
        const tickets24h = tickets.filter(t => new Date(t.createdAt) > oneDayAgo);
        const resolved24h = tickets24h.filter(t => t.status === 'resolvido' || t.status === 'fechado');
        const open24h = tickets24h.filter(t => t.status === 'aberto');

        // Total Resolved (All time)
        const totalResolved = tickets.filter(t => t.status === 'resolvido' || t.status === 'fechado').length;

        // --- 1. Real Activity by Hour (24h) ---
        // Initialize 24h buckets (0-23)
        const activityByHour = Array(24).fill(0).map((_, i) => ({
            hour: `${i}h`,
            tickets: 0,
            resolved: 0
        }));

        tickets24h.forEach(t => {
            const date = new Date(t.createdAt);
            const hour = date.getHours();
            if (activityByHour[hour]) {
                activityByHour[hour].tickets++;
            }
            if (t.status === 'resolvido' || t.status === 'fechado') {
                // Note: Ideally we use 'updatedAt' for resolution time, but if not available we approximate or check responses
                // For now, if resolved within 24h window, count it in creation hour (approximation) or updatedAt hour if exists
                const resolveTime = t.updatedAt ? new Date(t.updatedAt) : date;
                const resolveHour = resolveTime.getHours();
                if (activityByHour[resolveHour]) {
                    activityByHour[resolveHour].resolved++;
                }
            }
        });

        // --- 2. Real Top Performers ---
        const performerMap = {}; // { userId: { name, resolved, tickets } }

        tickets.forEach(t => {
            const involvedStaff = new Set();

            // Function to track staff member
            const trackStaff = (id, name = 'Unknown') => {
                if (!id) return;
                involvedStaff.add(id);

                if (!performerMap[id]) {
                    performerMap[id] = {
                        name: name !== 'Unknown' ? name : (performerMap[id]?.name || 'Unknown'),
                        resolved: 0,
                        tickets: 0,
                        id
                    };
                } else if (name !== 'Unknown') {
                    // Update name if we found a better one
                    performerMap[id].name = name;
                }
            };

            // 1. Check assignedTo (single ID)
            if (t.assignedTo) trackStaff(t.assignedTo);

            // 2. Check assignees (array of objects {discordId, name, ...})
            if (t.assignees && Array.isArray(t.assignees)) {
                t.assignees.forEach(a => trackStaff(a.discordId, a.name));
            }

            // Increment ticket count for involved staff
            involvedStaff.forEach(staffId => {
                // Ensure the staffId exists in performerMap before incrementing
                if (!performerMap[staffId]) {
                    performerMap[staffId] = { name: 'Unknown', resolved: 0, tickets: 0, id: staffId };
                }
                performerMap[staffId].tickets++;

                // Check if resolved
                if (t.status === 'resolvido' || t.status === 'fechado') {
                    performerMap[staffId].resolved++;
                }

                // Try to resolve name from allUsers if still Unknown
                if (performerMap[staffId].name === 'Unknown' && allUsers) {
                    const user = allUsers.find(u => u.discordId === staffId);
                    if (user) performerMap[staffId].name = getStaffDisplayName(user);
                }
            });
        });

        const topPerformers = Object.values(performerMap)
            .filter(p => p.name !== 'Unknown') // Filter out unknown users if possible, or keep them if you want to show missing data
            .sort((a, b) => b.tickets - a.tickets) // Sort by volume
            .slice(0, 5);


        // --- 3. Category Statistics ---
        const categoryCount = {};
        tickets.forEach(t => {
            const cat = t.category || "Sem Categoria";
            categoryCount[cat] = (categoryCount[cat] || 0) + 1;
        });

        let mostCreated = { name: '-', count: 0 };
        let leastCreated = { name: '-', count: Infinity };

        Object.entries(categoryCount).forEach(([name, count]) => {
            if (count > mostCreated.count) mostCreated = { name, count };
            if (count < leastCreated.count) leastCreated = { name, count };
        });

        if (leastCreated.count === Infinity) leastCreated = { name: '-', count: 0 };


        return {
            totalTickets24h: tickets24h.length,
            resolved24h: resolved24h.length,
            open24h: open24h.length,
            totalResolvedAllTime: totalResolved,
            chartData: activityByHour,
            topPerformers,
            mostCreated,
            leastCreated
        };
    }, [tickets, allUsers]);

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title="Tickets (24h)"
                    value={metrics.totalTickets24h}
                    icon={Ticket}
                    color="text-blue-400"
                    bg="bg-blue-500/10"
                    trend="+5%"
                />
                <KPICard
                    title="Resolvidos (24h)"
                    value={metrics.resolved24h}
                    icon={CheckCircle2}
                    color="text-green-400"
                    bg="bg-green-500/10"
                    trend="+12%"
                />
                <KPICard
                    title={`Mais: ${metrics.mostCreated.name}`}
                    value={metrics.mostCreated.count}
                    icon={TrendingUp}
                    color="text-orange-400"
                    bg="bg-orange-500/10"
                    trend="Categoria Top"
                />
                <KPICard
                    title={`Menos: ${metrics.leastCreated.name}`}
                    value={metrics.leastCreated.count}
                    icon={AlertTriangle}
                    color="text-purple-400"
                    bg="bg-purple-500/10"
                    trend="Menor Fluxo"
                />
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Chart */}
                <Card className="lg:col-span-2 bg-black/40 border-white/10 backdrop-blur-md">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-indigo-400" />
                            Fluxo de Atendimentos (24h)
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={metrics.chartData}>
                                <defs>
                                    <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#34d399" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                                <XAxis dataKey="hour" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#000000cc', borderColor: '#ffffff20', borderRadius: '8px', color: '#fff' }}
                                    itemStyle={{ color: '#fff' }}
                                />
                                <Area type="monotone" dataKey="tickets" stroke="#818cf8" fillOpacity={1} fill="url(#colorTickets)" name="Novos Tickets" />
                                <Area type="monotone" dataKey="resolved" stroke="#34d399" fillOpacity={1} fill="url(#colorResolved)" name="Resolvidos" />
                                <Legend />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Top Performers / Activity Feed */}
                <Card className="bg-black/40 border-white/10 backdrop-blur-md">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Users className="h-5 w-5 text-yellow-400" />
                            Top Staff (Geral)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {metrics.topPerformers.length > 0 ? (
                                metrics.topPerformers.map((staff, i) => (
                                    <div key={staff.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                                        <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-xs font-bold text-indigo-400">
                                            #{i + 1}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-sm font-bold text-gray-200">{staff.name || 'Staff'}</h4>
                                            <p className="text-xs text-gray-500">Membro da Equipe</p>
                                        </div>
                                        <Badge variant="secondary" className="bg-green-500/10 text-green-400 border-0">
                                            {staff.tickets} Tck
                                        </Badge>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500 text-center py-4">Nenhum dado disponível</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

// Helper Component for KPI Cards
const KPICard = ({ title, value, icon: Icon, color, bg, trend }) => (
    <Card className="bg-black/40 border-white/10 backdrop-blur-md hover:bg-white/5 transition-all">
        <CardContent className="p-6">
            <div className="flex justify-between items-start">
                <div>
                    <p className="text-sm font-medium text-gray-400 mb-1">{title}</p>
                    <h3 className="text-2xl font-bold text-white">{value}</h3>
                    {trend && (
                        <span className={`text-xs font-medium ${trend.startsWith('+') ? 'text-green-400' : 'text-red-400'} flex items-center mt-1`}>
                            {trend} <span className="text-gray-500 ml-1">vs ontem</span>
                        </span>
                    )}
                </div>
                <div className={`p-3 rounded-xl ${bg}`}>
                    <Icon className={`h-6 w-6 ${color}`} />
                </div>
            </div>
        </CardContent>
    </Card>
);

// Import AreaChart/Area for the gradient effect
import { AreaChart, Area } from 'recharts';

export default OverviewTab;
